// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api';
import {
  selectClientId,
  selectClientProductsAccounts,
} from '@shared/data-access/slices/clients';
import {
  advisoryProposalIbans,
  buildAdvisoryProposalAccounts,
} from '@shared/data-access/transformers/proposals/advisory-proposal-accounts';

/**
 * The accounts under advice: the client accounts the advisory proposal covers,
 * whatever the custodian (Nuvara Inversis or any other institution).
 *
 * @param clientId - Client to read the proposal of. Defaults to the active client.
 */
export const useAdvisoryProposalAccounts = (clientId?: number | null) => {
  const activeClientId = useSelector(selectClientId);
  const sharedInstitutions = useSelector(selectClientProductsAccounts);

  const resolvedClientId = clientId ?? activeClientId;

  const { data: profileData, ...clientProfileQuery } =
    advisoryApi.useGetClientProfileQuery(
      { client_id: resolvedClientId! },
      { skip: !resolvedClientId },
    );

  const advisoryProposal = profileData?.advisory_proposal;

  const institutions = useMemo(
    () =>
      buildAdvisoryProposalAccounts(
        advisoryProposal?.institutions,
        sharedInstitutions,
      ),
    [advisoryProposal?.institutions, sharedInstitutions],
  );

  const ibans = useMemo(() => advisoryProposalIbans(institutions), [institutions]);

  return {
    advisoryProposal,
    institutions,
    ibans,
    isLoading: clientProfileQuery.isLoading,
  } as const;
};
